/**
 * Business-day arithmetic on the Eastern business calendar (#496).
 *
 * A general-purpose primitive, deliberately separate from the approval policy that first
 * needed it (`utils/approval-sla.ts`). "How many working days is that?" is a question this
 * app will ask again — late-fee grace periods, notice windows, response deadlines — and
 * the answer should not live inside whichever feature asked first.
 *
 * ── EVERY INPUT IS A BUSINESS DATE ──
 *
 * That means a midnight-UTC marker, the same currency as the rest of `date-utils.ts`, and
 * you read its calendar fields with the UTC getters. Passing a raw `Date.now()` is a bug:
 * after 8 p.m. Eastern the UTC day has already rolled over, so an evening call would ask
 * about tomorrow. Convert first with `etDayMs(atMs)`.
 *
 * ── WEEKENDS ARE STRUCTURAL, HOLIDAYS ARE DATA ──
 *
 * Saturday and Sunday are computed. Holidays come from a generated set
 * (`utils/generated/us-holidays.ts`, built by `scripts/generate-holidays.ts` from the
 * `date-holidays` devDependency), so the 10.9 MB library never reaches the Worker or the
 * Expo bundle and these functions stay SYNCHRONOUS — an async variant would infect every
 * caller down to a badge render.
 *
 * The `holidays` parameter is injectable, which is the seam for a future per-organization
 * closure table and what lets tests pin a synthetic calendar instead of asserting against
 * whatever year it happens to be.
 */

import { DAY_MS, formatBusinessDateIso, getBusinessDateParts } from './date-utils';
import { HOLIDAY_COVERAGE, US_HOLIDAYS } from './generated/us-holidays';

/**
 * A calendar with weekends but no holidays.
 *
 * Exported as an explicit, named degradation rather than something a caller assembles
 * inline: reaching for this means "I know I am giving up holiday accuracy", which is a
 * decision worth being able to grep for. `approval-sla.ts` falls back to it, loudly, when
 * a date lands outside the generated coverage window.
 */
export const WEEKENDS_ONLY: ReadonlySet<string> = new Set<string>();

/**
 * Thrown when a date falls outside the generated holiday window.
 *
 * The alternative — quietly treating an out-of-range date as holiday-free — is the failure
 * this whole design is arranged against: past the last generated year every holiday
 * becomes an ordinary business day and every derived deadline shifts a day early, with
 * nothing anywhere to notice. A throw makes the caller choose, and
 * `tests/utils/business-days.spec.ts` fails while there is still time to regenerate.
 */
export class HolidayCoverageError extends Error {
	constructor(public readonly year: number) {
		super(
			`${year} is outside the generated holiday calendar `
			+ `(${HOLIDAY_COVERAGE.fromYear}–${HOLIDAY_COVERAGE.toYear}). `
			+ 'Run `bun run holidays:generate` with a wider range in scripts/generate-holidays.ts.',
		);
		this.name = 'HolidayCoverageError';
	}
}

/**
 * Guard rail on the day-stepping loops below.
 *
 * A pathological holiday set — every day of a year listed — would otherwise spin forever
 * inside `addBusinessDays`. Ten years of steps is far past any real call (the largest
 * caller asks for 2) and cheap to check.
 */
const MAX_STEPS = 3_650;

/** Saturday or Sunday, read from the UTC fields because a business date IS a UTC marker. */
export function isWeekend(businessMs: number): boolean {
	const dow = new Date(businessMs).getUTCDay();
	return dow === 0 || dow === 6;
}

/**
 * Is this business date inside the generated calendar's window?
 *
 * Exported so a caller that wants to degrade gracefully can ask BEFORE it starts stepping,
 * rather than catching a throw from three frames down.
 */
export function isHolidayCoverageKnown(businessMs: number): boolean {
	const { year } = getBusinessDateParts(businessMs);
	return year >= HOLIDAY_COVERAGE.fromYear && year <= HOLIDAY_COVERAGE.toYear;
}

/**
 * A weekday that isn't an observed closure.
 *
 * Coverage is only enforced when using the DEFAULT calendar. A caller who injects its own
 * set owns that set's range — `WEEKENDS_ONLY` covers all of history by construction, and a
 * test calendar shouldn't have to span 2026–2040 to be usable.
 */
export function isBusinessDay(businessMs: number, holidays: ReadonlySet<string> = US_HOLIDAYS): boolean {
	if (holidays === US_HOLIDAYS && !isHolidayCoverageKnown(businessMs)) {
		throw new HolidayCoverageError(getBusinessDateParts(businessMs).year);
	}
	if (isWeekend(businessMs)) return false;
	return !holidays.has(formatBusinessDateIso(businessMs));
}

/**
 * The first business day at or after `businessMs`.
 *
 * Distinct from `addBusinessDays(ms, 0)`, which is the identity. "Snap a weekend onto
 * Monday" and "advance zero days" are different intents and conflating them is how a
 * deadline lands on a Saturday.
 */
export function nextBusinessDay(businessMs: number, holidays: ReadonlySet<string> = US_HOLIDAYS): number {
	let cur = businessMs;
	for (let i = 0; i < MAX_STEPS; i += 1) {
		if (isBusinessDay(cur, holidays)) return cur;
		cur += DAY_MS;
	}
	throw new Error(`No business day found within ${MAX_STEPS} days of ${formatBusinessDateIso(businessMs)}`);
}

/**
 * `n` business days STRICTLY AFTER `businessMs`.
 *
 * Strictly-after is the semantic every caller has wanted so far: "you have two business
 * days to approve this" starts counting tomorrow, not today — the day something lands in
 * your queue at 4:55 p.m. is not one of your working days on it.
 *
 * `n = 0` returns the input untouched, weekend or not. Use `nextBusinessDay` to snap.
 */
export function addBusinessDays(
	businessMs: number,
	n: number,
	holidays: ReadonlySet<string> = US_HOLIDAYS,
): number {
	if (!Number.isFinite(n) || n <= 0) return businessMs;
	let cur = businessMs;
	let remaining = Math.floor(n);
	for (let i = 0; i < MAX_STEPS && remaining > 0; i += 1) {
		cur += DAY_MS;
		if (isBusinessDay(cur, holidays)) remaining -= 1;
	}
	if (remaining > 0) {
		throw new Error(`Could not advance ${n} business days within ${MAX_STEPS} days`);
	}
	return cur;
}

/**
 * Business days from `fromMs` to `toMs`, counting the end and not the start.
 *
 * The mirror of `addBusinessDays`, so the two compose: if
 * `addBusinessDays(from, 2) === due`, then `businessDaysBetween(from, due) === 2`. A
 * caller can therefore express "how far through its window is this?" without re-deriving
 * the window.
 *
 * Returns 0 when `toMs` is at or before `fromMs` — the past is not negative progress, and
 * a signed answer here would only ever be read as `> N` anyway.
 */
export function businessDaysBetween(
	fromMs: number,
	toMs: number,
	holidays: ReadonlySet<string> = US_HOLIDAYS,
): number {
	if (toMs <= fromMs) return 0;
	let count = 0;
	let cur = fromMs;
	for (let i = 0; i < MAX_STEPS && cur < toMs; i += 1) {
		cur += DAY_MS;
		if (isBusinessDay(cur, holidays)) count += 1;
	}
	return count;
}
